"use client";

import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { useClassGet, useTeacherGetPublic } from "@/hooks";
import {
  buildClassPublicPath,
  buildTeacherPublicPath,
  PUBLIC_LOCATION_RETURN_CLASS_ID,
  PUBLIC_TEACHER_RETURN_CLASS_ID,
} from "@/lib/utils/publicPaths";
import { getTeacherPublicShortName } from "@/lib/utils/teacherPublic";
import {
  consumeReturnClassIdFromSessionStorage,
  getLocationsListingBackLink,
  getPublicDetailPageDefaultBackLink,
  getTeachersListingBackLink,
  parseClassIdFromClassesReferrer,
  parseFromClassQueryValue,
  parseFromListingQueryValue,
  parseFromTeacherQueryValue,
  parseListingFromReferrer,
  parseTeacherIdFromTeachersReferrer,
  PUBLIC_FROM_CLASS_QUERY,
  PUBLIC_FROM_QUERY,
  PUBLIC_FROM_TEACHER_QUERY,
  type PublicBackLink,
  type PublicDetailPage,
  type PublicReturnListing,
} from "@/lib/utils/publicReturnToClass";
import { parseListingHrefFromReferrer, readPublicListingHref } from "@/lib/utils/publicListingReturn";

export interface PublicPageReturnContext {
  returnClassId: number | null;
  returnTeacherId: number | null;
  fromListing: PublicReturnListing | null;
  backLink: PublicBackLink;
}

export interface PublicPageReturnContextOptions {
  /** Which detail page is using this hook - sets the default back link. */
  page: PublicDetailPage;
}

/**
 * Resolves back navigation for public teacher/location detail pages.
 * See `publicReturnToClass.ts` for priority order.
 */
export function usePublicPageReturnContext(
  options: PublicPageReturnContextOptions,
): PublicPageReturnContext {
  const { page } = options;
  const searchParams = useSearchParams();

  const returnClassIdFromQuery = useMemo(
    () => parseFromClassQueryValue(searchParams.get(PUBLIC_FROM_CLASS_QUERY)),
    [searchParams],
  );

  const returnTeacherIdFromQuery = useMemo(() => {
    if (returnClassIdFromQuery != null) return null;
    return parseFromTeacherQueryValue(searchParams.get(PUBLIC_FROM_TEACHER_QUERY));
  }, [searchParams, returnClassIdFromQuery]);

  const fromListingFromQuery = useMemo(() => {
    if (returnClassIdFromQuery != null || returnTeacherIdFromQuery != null) {
      return null;
    }
    return parseFromListingQueryValue(searchParams.get(PUBLIC_FROM_QUERY));
  }, [searchParams, returnClassIdFromQuery, returnTeacherIdFromQuery]);

  const [fallbackClassId, setFallbackClassId] = useState<number | null>(null);
  const [fallbackTeacherId, setFallbackTeacherId] = useState<number | null>(null);
  const [fallbackListing, setFallbackListing] = useState<PublicReturnListing | null>(
    null,
  );
  const [listingHref, setListingHref] = useState<string | null>(null);

  useEffect(() => {
    if (
      returnClassIdFromQuery != null ||
      returnTeacherIdFromQuery != null ||
      fromListingFromQuery != null
    ) {
      try {
        sessionStorage.removeItem(PUBLIC_TEACHER_RETURN_CLASS_ID);
        sessionStorage.removeItem(PUBLIC_LOCATION_RETURN_CLASS_ID);
      } catch {
        /* private mode */
      }
      return;
    }

    const idStorage = consumeReturnClassIdFromSessionStorage();
    if (idStorage != null) {
      setFallbackClassId(idStorage);
      return;
    }

    const idRef = parseClassIdFromClassesReferrer();
    if (idRef != null) {
      setFallbackClassId(idRef);
      return;
    }

    const teacherRef = parseTeacherIdFromTeachersReferrer();
    if (teacherRef != null) {
      setFallbackTeacherId(teacherRef);
      return;
    }

    const listingRef = parseListingFromReferrer();
    if (listingRef != null) {
      setFallbackListing(listingRef);
    }
  }, [returnClassIdFromQuery, returnTeacherIdFromQuery, fromListingFromQuery]);

  const returnClassId = returnClassIdFromQuery ?? fallbackClassId;
  const returnTeacherId = returnTeacherIdFromQuery ?? fallbackTeacherId;
  const fromListing = fromListingFromQuery ?? fallbackListing;

  useEffect(() => {
    if (fromListing !== "teachers" && fromListing !== "locations") {
      setListingHref(null);
      return;
    }
    setListingHref(
      readPublicListingHref(fromListing) ??
        parseListingHrefFromReferrer(fromListing),
    );
  }, [fromListing]);

  const {
    data: classData,
    isPending: isClassPending,
    isError: isClassError,
  } = useClassGet(returnClassId ?? 0, {
    enabled: returnClassId != null && returnClassId > 0,
  });

  const {
    data: teacherData,
    isPending: isTeacherPending,
    isError: isTeacherError,
  } = useTeacherGetPublic(returnTeacherId ?? 0, {
    enabled: returnTeacherId != null && returnTeacherId > 0,
  });

  const pageDefaultBackLink = useMemo(
    () => getPublicDetailPageDefaultBackLink(page),
    [page],
  );

  const backLink = useMemo((): PublicBackLink => {
    if (returnClassId != null) {
      if (isClassPending || isClassError || !classData) {
        return pageDefaultBackLink;
      }
      const href =
        classData.slug != null && String(classData.slug).length > 0
          ? `/classes/${classData.slug}`
          : buildClassPublicPath({
              name: classData.name || "class",
              classId: Number(classData.classId),
            });
      return {
        href,
        label: `Back to ${classData.name || "class"}`,
      };
    }

    if (returnTeacherId != null) {
      if (isTeacherPending || isTeacherError || !teacherData) {
        return pageDefaultBackLink;
      }
      const teacherName = getTeacherPublicShortName(teacherData);
      const href = buildTeacherPublicPath({
        firstName: teacherData.firstName,
        lastName: teacherData.lastName,
        teacherId: Number(teacherData.teacherId),
      });
      return {
        href,
        label: `Back to ${teacherName}`,
      };
    }

    if (fromListing === "teachers") {
      return {
        href: listingHref ?? getTeachersListingBackLink().href,
        label: getTeachersListingBackLink().label,
      };
    }

    if (fromListing === "locations") {
      return {
        href: listingHref ?? getLocationsListingBackLink().href,
        label: getLocationsListingBackLink().label,
      };
    }

    return pageDefaultBackLink;
  }, [
    returnClassId,
    returnTeacherId,
    fromListing,
    isClassPending,
    isClassError,
    classData,
    isTeacherPending,
    isTeacherError,
    teacherData,
    pageDefaultBackLink,
    listingHref,
  ]);

  return {
    returnClassId,
    returnTeacherId,
    fromListing,
    backLink,
  };
}
